import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import Title from '../../components/Title';
import { axiosKK } from '../../services/networkRequest';
import { PreviousOrderListResponse, RemovalOrdersResponse, Order, MyAccountResponse } from '../../models/APIModels';
import UserContext from '../../contexts/UserContext';
import OrderEditDeliveryDetailsModal from '../../components/edit/OrderEditDeliveryDetailsModal';
import { OrderSelectionModal } from '../../components/OrderSelectionModal';
import { ActiveOrderCard } from '../../components/user/ActiveOrderCard';
import { OrderCardSkeleton } from '../../components/user/OrderCardSkeleton';
import { NextPaymentResponse } from '../../models/APIModels';
import { ExtraServicesModal } from '../../components/modals/ExtraServicesModal';
import { isOrderCompleted } from '../../utilities/OrderHelpers';
import {
  loadDashboardCache,
  saveDashboardCache,
  clearDashboardCache,
  convertPaymentsObjectToMap,
} from '../../utilities/DashboardCache';

// Helper to check if value is truly empty (handles string "undefined" and "null" from API)
const isEmptyValue = (value: any): boolean => {
  return !value || value === 'undefined' || value === 'null' || value === '';
};

function DashboardComponent({ history }: RouteComponentProps): JSX.Element {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showOrderSelectionModal, setShowOrderSelectionModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showExtraServicesModal, setShowExtraServicesModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<number>>(new Set());
  const [nextPayments, setNextPayments] = useState<Map<number, NextPaymentResponse>>(new Map());
  const [totalBoxDays, setTotalBoxDays] = useState<number | undefined>(undefined);
  const [membershipTier, setMembershipTier] = useState<string | undefined>(undefined);
  const [showPointsInfo, setShowPointsInfo] = useState(false);
  const { user } = useContext(UserContext);

  useEffect(() => {
    window.scrollTo(0, 0);
    let isMounted = true;
    setIsLoading(true);

    // Try to load from cache first
    const cachedData = loadDashboardCache();
    if (cachedData) {
      setOrders(cachedData.orders);
      setNextPayments(convertPaymentsObjectToMap(cachedData.nextPayments));
      if (cachedData.totalBoxDays !== undefined) {
        setTotalBoxDays(cachedData.totalBoxDays);
      }
      if (cachedData.membershipTier !== undefined) {
        setMembershipTier(cachedData.membershipTier);
      }
      setIsLoading(false);
    }

    // Fetch fresh data in the background
    Promise.all([
      axiosKK.get<PreviousOrderListResponse>('/order/previous'),
      axiosKK.get<RemovalOrdersResponse>('/removals/orders').catch(() => ({ data: { orders: [], nextPayments: {} } })),
      axiosKK.get<MyAccountResponse>('/customer/account')
    ]).then(([regularOrdersResult, removalOrdersResult, accountResult]) => {
      if (!isMounted) return;

      // Merge both types of orders
      const allOrders = [
        ...regularOrdersResult.data.previousOrders,
        ...removalOrdersResult.data.orders
      ];

      // Sort by creation date (most recent first)
      const sortedOrders = allOrders.sort((a, b) => b.createdAt - a.createdAt);
      setOrders(sortedOrders);

      // Merge nextPayments from both responses
      const paymentsMap = new Map<number, NextPaymentResponse>();

      // Handle regular orders nextPayments
      if (regularOrdersResult.data.nextPayments) {
        Object.entries(regularOrdersResult.data.nextPayments).forEach(([key, value]) => {
          paymentsMap.set(Number(key), value);
        });
      }

      // Handle removal orders nextPayments
      if (removalOrdersResult.data.nextPayments) {
        Object.entries(removalOrdersResult.data.nextPayments).forEach(([key, value]) => {
          paymentsMap.set(Number(key), value);
        });
      }

      setNextPayments(paymentsMap);

      // Update totalBoxDays and membershipTier from account data
      const boxDays = accountResult.data.totalBoxDays;
      if (boxDays !== undefined) {
        setTotalBoxDays(boxDays);
      }

      const tier = accountResult.data.membershipTier;
      if (tier !== undefined) {
        setMembershipTier(tier);
      }

      // Save to cache including totalBoxDays and membershipTier
      saveDashboardCache(sortedOrders, paymentsMap, boxDays, tier);

      setIsLoading(false);
    }).catch((error) => {
      if (!isMounted) return;
      console.error('Error fetching dashboard data:', error);
      // If regular orders fail and we have no cache, set empty array
      if (!cachedData) {
        setOrders([]);
      }
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const activeOrders = orders.filter(order => !isOrderCompleted(order));
  const completedOrders = orders.filter(order => isOrderCompleted(order));
  const displayOrders = activeOrders;

  const eligibleOrdersForReturn = orders.filter(
    (order) =>
      order.storageRequired && !order.suitcaseOrder && isEmptyValue(order.deliveryDate),
  );

  const handleReturnItems = () => {
    if (eligibleOrdersForReturn.length > 0) {
      setShowOrderSelectionModal(true);
    } else {
      history.push('/');
    }
  };

  const handleOrderSelection = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderSelectionModal(false);
    setShowDeliveryModal(true);
  };

  const handleDeliveryModalClose = () => {
    setShowDeliveryModal(false);
    setSelectedOrder(null);
  };

  const handleDeliveryUpdate = (updatedOrder: Order) => {
    setOrders((prevOrders) => {
      const updatedOrders = prevOrders.map((order) =>
        order.id === updatedOrder.id ? updatedOrder : order,
      );
      clearDashboardCache();
      return updatedOrders;
    });
    handleDeliveryModalClose();
  };

  const toggleOrderExpansion = (orderId: number) => {
    setExpandedOrderIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleViewOrderDetails = (orderId: number, orderType?: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (orderType === 'removal') {
      history.push(`/removals/order/${orderId}`);
    } else {
      history.push(`/order/${orderId}`);
    }
  };

  const handleRequestReturn = (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOrder(order);
    setShowDeliveryModal(true);
  };

  const handleNavigateToPastOrders = () => {
    history.push({
      pathname: '/past-orders',
      state: {
        allOrders: orders,
        nextPayments: Object.fromEntries(nextPayments),
        isLoading
      }
    });
  };

  return (
    <>
      <div className="dashboard-title-wrapper">
        <Title>My Keepr</Title>
      </div>
      <div className="dashboard-header-container">
        {user && <p className="grey welcome-message">Welcome back, {user?.firstName}</p>}
        {(membershipTier !== undefined || totalBoxDays !== undefined) && (
          <div className="membership-tier-metric">
            <p>
              Keepr Points: <strong className="points-value">{totalBoxDays || 0}</strong>
              <button
                className="info-icon-btn"
                onClick={() => setShowPointsInfo(!showPointsInfo)}
                onMouseEnter={() => setShowPointsInfo(true)}
                onMouseLeave={() => setShowPointsInfo(false)}
                aria-label="Info about Keepr Points"
              >
                ⓘ
              </button>
            </p>
            {showPointsInfo && (
              <div className="info-tooltip">
                Beta Feature more coming soon
              </div>
            )}
            {membershipTier && (
              <p className={`tier-badge tier-${membershipTier.toLowerCase()}`}>
                <span className="tier-name">
                  {membershipTier.charAt(0).toUpperCase() + membershipTier.slice(1).toLowerCase()}
                </span>
              </p>
            )}
          </div>
        )}
      </div>

      <div className="active-orders-section">
        {isLoading && displayOrders.length === 0 ? (
          <OrderCardSkeleton />
        ) : displayOrders.length > 0 ? (
          displayOrders.map((order) => {
            const nextPaymentData = nextPayments.get(order.id);
            const isExpanded = expandedOrderIds.has(order.id);
            
            return (
              <ActiveOrderCard
                key={order.id}
                order={order}
                nextPaymentData={nextPaymentData}
                isExpanded={isExpanded}
                onToggle={() => toggleOrderExpansion(order.id)}
                onRequestReturn={order.storageRequired && !order.suitcaseOrder && isEmptyValue(order.deliveryDate)
                  ? (e) => handleRequestReturn(order, e)
                  : undefined}
                onViewDetails={(e) => handleViewOrderDetails(order.id, order.orderType, e)}
              />
            );
          })
        ) : null}
      </div>
      
      <div className="dashboard-all-box-container customer-dashboard">
        <section onClick={() => history.push('/')} className="each-box">
          <div className="content">
            <span className="action-icon">📦</span>
            <h3>Make New Bookings!</h3>
          </div>
          <button className="dashboard-action-btn">Book Now</button>
        </section>
        <section onClick={() => history.push('/profile')} className="each-box">
          <div className="content">
            <span className="action-icon">👤</span>
            <h3>Name, Address and Payment Details</h3>
          </div>
          <button className="dashboard-action-btn">Manage Profile</button>
        </section>
        <section onClick={() => history.push('/payments')} className="each-box">
          <div className="content">
            <span className="action-icon">💳</span>
            <h3>Recurring Payments and Transactions</h3>
          </div>
          <button className="dashboard-action-btn">Track My Payments</button>
        </section>
        {eligibleOrdersForReturn.length > 0 && (
          <section onClick={handleReturnItems} className="each-box">
            <div className="content">
              <span className="action-icon">↩️</span>
              <h3>Request Return of Your Items</h3>
            </div>
            <button className="dashboard-action-btn">Return My Items</button>
          </section>
        )}
        <section onClick={() => setShowExtraServicesModal(true)} className="each-box">
          <div className="content">
            <span className="action-icon">✨</span>
            <h3>Extra services...</h3>
          </div>
          <button className="dashboard-action-btn">View Services</button>
        </section>
        {completedOrders.length > 0 && (
          <section onClick={handleNavigateToPastOrders} className="each-box">
            <div className="content">
              <span className="action-icon">📋</span>
              <h3>Past orders</h3>
            </div>
            <button className="dashboard-action-btn">View Past Orders</button>
          </section>
        )}
        <section onClick={() => history.push('/update-payment-method')} className="each-box">
          <div className="content">
            <span className="action-icon">🔄</span>
            <h3>Update Payment Method</h3>
          </div>
          <button className="dashboard-action-btn">Update Payment</button>
        </section>
      </div>

      <div className="line" />

      <OrderSelectionModal
        isOpen={showOrderSelectionModal}
        orders={orders}
        onSelectOrder={handleOrderSelection}
        onClose={() => setShowOrderSelectionModal(false)}
      />

      {selectedOrder && (
        <OrderEditDeliveryDetailsModal
          editMode={showDeliveryModal}
          order={selectedOrder}
          close={handleDeliveryModalClose}
          update={handleDeliveryUpdate}
        />
      )}

      <ExtraServicesModal
        isOpen={showExtraServicesModal}
        onClose={() => setShowExtraServicesModal(false)}
      />
    </>
  );
}

export default withRouter(DashboardComponent);

